/**
 * Unity WebGL Build Configuration
 *
 * SINGLE SOURCE OF TRUTH for Unity build paths and settings.
 * Points to the FlexoRoom build on CDN (flexoGame with Synty characters).
 *
 * Build files are at: cdn.gamedate.co.il/unity/flexo-room/Build/
 * Source build artifact: flexoGame/flexoGame-built/flexoGame/Build/
 * Files are renamed from flexoGame.* to FlexoRoom.* during CDN upload.
 *
 * CACHE KEYS: each file carries its own content-hash `?v=` (generated into
 * unityBuildHashes.json by scripts/gen-unity-hashes.mjs, run by deploy.sh).
 * Browser HTTP cache AND Unity's IndexedDB data cache key on the full URL, so
 * a code-only Unity rebuild no longer forces users to re-download the ~40 MB
 * data file — only files whose bytes actually changed get a new key.
 */

import buildHashes from './unityBuildHashes.json';

// Human-readable build label (logs/telemetry only — NOT a cache key anymore).
export const UNITY_BUILD_VERSION = '2026-09-08-v97-sticky-go';

// Base path where Unity build is served (CDN for static assets)
export const UNITY_BUILD_BASE = 'https://cdn.gamedate.co.il/unity/flexo-room';

type HashedFile = { name: string; hash: string; size: number };
const FILES = buildHashes.files as Record<'loader' | 'framework' | 'wasm' | 'data', HashedFile>;

const url = (f: HashedFile) => `${UNITY_BUILD_BASE}/Build/${f.name}?v=${f.hash}`;

// Computed file paths - FlexoRoom build uses predictable names + per-file hash keys
export const UNITY_BUILD_PATHS = {
  loaderUrl: url(FILES.loader),
  frameworkUrl: url(FILES.framework),
  codeUrl: url(FILES.wasm),
  dataUrl: url(FILES.data),
  streamingAssetsUrl: `${UNITY_BUILD_BASE}/StreamingAssets`,
} as const;

// Unity instance metadata
export const UNITY_METADATA = {
  companyName: 'GameDate',
  productName: 'FlexoRoom',
  productVersion: '1.0',
} as const;

// Full Unity configuration object
export const unityBuildConfig = {
  ...UNITY_BUILD_PATHS,
  ...UNITY_METADATA,
  autoSyncPersistentDataPath: true,
} as const;

// File sizes for preloader progress (exact compressed bytes, from the hash manifest)
export const UNITY_FILE_SIZES = {
  loader: FILES.loader.size,
  framework: FILES.framework.size,
  wasm: FILES.wasm.size,
  data: FILES.data.size,
} as const;

// Preload file list for UnityPreloader service (same URLs as the loader uses,
// so a completed preload = guaranteed HTTP-cache hits for Unity's own fetches)
export const UNITY_PRELOAD_FILES = [
  { path: `/Build/${FILES.loader.name}?v=${FILES.loader.hash}`, size: FILES.loader.size },
  { path: `/Build/${FILES.framework.name}?v=${FILES.framework.hash}`, size: FILES.framework.size },
  { path: `/Build/${FILES.wasm.name}?v=${FILES.wasm.hash}`, size: FILES.wasm.size },
  { path: `/Build/${FILES.data.name}?v=${FILES.data.hash}`, size: FILES.data.size },
] as const;

export type UnityBuildConfig = typeof unityBuildConfig;
